/* Seyasha smart ordering (merchandising engine).

   What it does
   - Builds a visitor profile from: the ad or link they came from (UTM tags, ad set names, click ids, referrer,
     in-app browser), the page they landed on, collections and products they look at, time spent on product
     pages, sizes picked, wishlist and bag adds, coupon interest, searches, device and time of day, browser
     language, and for signed-in customers their past orders and customer tags.
   - Orders collection grids, homepage rails and "You may also like" for that visitor, and keeps the same design
     from sitting side by side. New visitors get a fresh, varied order every day instead of "newest first".
   - Marks the visitor with a segment (persona, life stage, shopping for, age band from the ad, first source,
     intent). The segment is written to the bag as hidden attributes, so it shows on the order in Shopify admin,
     and is sent to Google Analytics as user properties when analytics is allowed.

   Privacy
   - The profile is kept only in this browser (localStorage "sy-mx") and only when the visitor's privacy choice
     allows preferences or analytics (Shopify customer privacy API). Otherwise it lives for one page only.
   - Add ?sy_debug=1 to any URL to see the profile and scores; ?sy_debug=0 to hide; the panel can reset it. */
(function () {
  'use strict';
  if (window.SYM) return;
  var W = window, D = document, NOW = Date.now(), DAY = 864e5, HL = 14 * DAY;
  var SYM = (W.SYM = {});

  function readJSON(id) {
    var el = D.getElementById(id);
    if (!el) return null;
    try { return JSON.parse(el.textContent || 'null'); } catch (e) { return null; }
  }
  var cfg = readJSON('sy-mx-cfg') || {};
  if (cfg.prod && cfg.prod.facts) { for (var fk in cfg.prod.facts) if (!(fk in cfg.prod)) cfg.prod[fk] = cfg.prod.facts[fk]; }
  var ROOT = cfg.root || (W.Shopify && Shopify.routes && Shopify.routes.root) || '/';
  var STRENGTH = { off: 0, gentle: 0.8, balanced: 1.6, strong: 2.4 }[cfg.str || 'balanced'];
  if (STRENGTH === undefined) STRENGTH = 1.6;

  /* ---------- reference tables ---------- */
  var LABEL = {
    office: 'Office humour', hindi: 'Hindi & desi words', college: 'College & hostel', bff: 'Friends & squad',
    couple: 'Couples', zodiac: 'Zodiac', adulting: 'Adulting', daily: 'Everyday desi life', money: 'Money & hustle',
    funny: 'Funny graphics', minimal: 'Minimal designs'
  };
  /* how an interest reads in "Because you like ..." */
  var LIKE = {
    office: 'office humour', hindi: 'Hindi and desi words', college: 'college life', bff: 'designs for friends', couple: 'couple designs',
    zodiac: 'zodiac designs', adulting: 'adulting jokes', daily: 'everyday desi life', money: 'Money Follows', funny: 'funny graphics', minimal: 'minimal designs'
  };
  /* collection handle -> signal: theme, g:garment, a:audience, x:merch list */
  var COLMAP = {
    'funny-office-t-shirts': 'office', 'hindi-typography': 'hindi', 'college-life-t-shirts': 'college',
    'best-friend-t-shirts': 'bff', 'couple-t-shirts': 'couple', 'zodiac': 'zodiac', 'funny-adulting-t-shirts': 'adulting',
    'daily-struggle-t-shirts': 'daily', 'money-follows': 'money', 'funny-t-shirts': 'funny',
    'oversized-t-shirts': 'g:tee', 'regular-t-shirts': 'g:reg', 'oversized-hoodies': 'g:hoodie', 'hoodies-sweatshirts': 'g:hoodie',
    'for-women': 'a:her', 'for-men': 'a:him', 'new-arrivals': 'x:new', 'bestsellers': 'x:pick', 'trending': 'x:trend'
  };
  /* theme -> collection used for "Picked for you" */
  var THCOL = {
    office: 'funny-office-t-shirts', hindi: 'hindi-typography', college: 'college-life-t-shirts', bff: 'best-friend-t-shirts',
    zodiac: 'zodiac', adulting: 'funny-adulting-t-shirts', daily: 'daily-struggle-t-shirts', money: 'money-follows',
    funny: 'funny-t-shirts', tee: 'oversized-t-shirts', reg: 'regular-t-shirts', hoodie: 'oversized-hoodies', sweat: 'hoodies-sweatshirts'
  };
  /* words in ad names, UTM tags and searches -> signals */
  var WORDS = [
    ['office', /\b(office|offices|corporate|corp|work|working|wfh|wfo|desk|boss|meeting|meetings|appraisal|manager|hr|monday|job|jobs|colleague|colleagues|kpi|deadline|excel|standup|client|startup|linkedin|professional|professionals|employee)\b/],
    ['college', /\b(college|campus|hostel|student|students|exam|exams|bunk|semester|engineering|engineer|iit|nit|mba|attendance|fresher|freshers|uni|university|genz|gen-z)\b/],
    ['hindi', /\b(hindi|desi|devanagari|typography|hinglish|bolo|shabd|akshar|bharat|bhasha)\b/],
    ['bff', /\b(bff|friend|friends|friendship|yaar|yaari|squad|dost|dosti|gang|buddy|buddies|matching)\b/],
    ['couple', /\b(couple|couples|valentine|valentines|bae|partner|husband|wife|boyfriend|girlfriend|love)\b/],
    ['zodiac', /\b(zodiac|rashi|aries|taurus|gemini|cancer|leo|virgo|libra|scorpio|sagittarius|capricorn|aquarius|pisces|astro|astrology|horoscope)\b/],
    ['adulting', /\b(adulting|adult|emi|rent|salary|tax|taxes|bills|selfcare|self-care|personal|30s)\b/],
    ['daily', /\b(chai|daily|struggle|traffic|pothole|commute|monsoon|everyday)\b/],
    ['money', /\b(money|rich|paisa|invest|investing|stocks|crypto|hustle|finance|wealth)\b/],
    ['funny', /\b(funny|meme|memes|humour|humor|joke|jokes|sarcasm|sarcastic|lol)\b/],
    ['minimal', /\b(minimal|minimalist|line|lineart|clean|simple|aesthetic)\b/],
    ['g:hoodie', /\b(hoodie|hoodies|sweatshirt|sweatshirts|winter|warm)\b/],
    ['g:tee', /\b(oversized|baggy|tee|tees|tshirt|tshirts|t-shirt|t-shirts)\b/],
    ['g:reg', /\b(regular|fitted|slim)\b/],
    ['a:her', /\b(women|woman|girls|girl|her|female|ladies|womens)\b/],
    ['a:him', /\b(men|man|boys|guys|him|male|mens)\b/],
    ['i:gift', /\b(gift|gifts|gifting|birthday|diwali|rakhi|christmas|santa|anniversary)\b/],
    ['i:deal', /\b(sale|deal|deals|offer|offers|discount|discounts|coupon|cheap|budget)\b/],
    ['l:student', /\b(student|students|college|hostel|exam|exams|genz|gen-z|fresher|freshers)\b/],
    ['l:pro', /\b(corporate|office|professional|professionals|working|employee|manager|wfh|wfo)\b/]
  ];
  var SIZE_RE = /^(xxs|xs|s|m|l|xl|xxl|2xl|xxxl|3xl|4xl|5xl)$/i;

  /* ---------- helpers ---------- */
  function $$(s, r) { return Array.prototype.slice.call((r || D).querySelectorAll(s)); }
  function dec(e) { return e ? e[0] * Math.pow(0.5, (NOW - e[1]) / HL) : 0; }
  function add(m, k, a) { if (!k || !m) return; m[k] = [Math.min(dec(m[k]) + a, 40), NOW]; }
  function sum(m) { var s = 0; for (var k in m) s += dec(m[k]); return s; }
  function norm(m, k0) { var s = sum(m), o = {}; for (var k in m) o[k] = dec(m[k]) / (s + k0); return o; }
  function topOf(m) { var b = null, bv = 0; for (var k in m) { var v = dec(m[k]); if (v > bv) { bv = v; b = k; } } return b ? { k: b, w: bv } : null; }
  function hash(s) { var h = 2166136261; for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  function rand(seed, key) { var x = hash(seed + '|' + key); x ^= x << 13; x ^= x >>> 17; x ^= x << 5; return ((x >>> 0) % 10000) / 10000; }
  function rid() { var s = ''; for (var i = 0; i < 12; i++) s += 'abcdefghijkmnpqrstuvwxyz23456789'.charAt(Math.floor(Math.random() * 32)); return s; }
  function esc(s) { return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; }); }
  function money(c) {
    var fmt = String(cfg.fmt || 'Rs. {{amount}}').replace(/<[^>]*>/g, '');
    var r = c / 100, whole = Math.abs(r - Math.round(r)) < 0.005;
    var n = r.toLocaleString('en-IN', { minimumFractionDigits: whole ? 0 : 2, maximumFractionDigits: whole ? 0 : 2 });
    return /\{\{\s*\w+\s*\}\}/.test(fmt) ? fmt.replace(/\{\{\s*\w+\s*\}\}/, n) : n;
  }

  /* ---------- profile ---------- */
  var KEY = 'sy-mx', canStore = null, canMark = null, P = null, dirty = false;
  function blank() {
    return { v: 1, id: rid(), c: NOW, s: 0, last: 0, src: {}, th: {}, gm: {}, co: {}, pl: {}, au: {}, ls: {}, it: {}, sz: {}, pr: [0, 0], seen: [], cart: [], bought: [], ad: {}, fl: {}, ses: {} };
  }
  try { P = JSON.parse(localStorage.getItem(KEY) || 'null'); } catch (e) { P = null; }
  if (!P || P.v !== 1) P = blank();
  var newSession = !P.last || NOW - P.last > 30 * 60e3;
  if (newSession) { P.s = (P.s || 0) + 1; P.ses = { t: NOW, pv: 0 }; }
  P.last = NOW;
  P.ses.pv = (P.ses.pv || 0) + 1;
  dirty = true;

  function save() {
    if (canStore !== true || !dirty) return;
    try { localStorage.setItem(KEY, JSON.stringify(P)); dirty = false; } catch (e) {}
  }
  /* Keeping the profile needs preferences or analytics consent. Marking (bag attributes, analytics user
     properties) needs analytics consent. */
  function consentState() {
    var cp = W.Shopify && Shopify.customerPrivacy;
    if (!cp || typeof cp.analyticsProcessingAllowed !== 'function') return null;
    try {
      var an = !!cp.analyticsProcessingAllowed();
      var pref = typeof cp.preferencesProcessingAllowed === 'function' ? !!cp.preferencesProcessingAllowed() : an;
      return { store: pref || an, mark: an };
    } catch (e) { return null; }
  }
  function onConsent() {
    var c = consentState();
    if (!c) return;
    canStore = c.store;
    canMark = c.mark;
    if (canStore) { dirty = true; save(); }
    else { try { localStorage.removeItem(KEY); } catch (e) {} }
    if (canMark) mark();
  }
  function watchConsent() {
    D.addEventListener('visitorConsentCollected', function () { setTimeout(onConsent, 0); });
    if (consentState() !== null) return onConsent();
    if (W.Shopify && Shopify.loadFeatures) {
      try { Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (err) { if (!err) onConsent(); }); } catch (e) {}
    }
  }
  W.addEventListener('pagehide', save);
  D.addEventListener('visibilitychange', function () { if (D.visibilityState === 'hidden') save(); });

  /* ---------- reading signals ---------- */
  function apply(sig, w) {
    if (!sig) return;
    var i = sig.indexOf(':');
    if (i < 0) return add(P.th, sig, w);
    var k = sig.slice(0, i), v = sig.slice(i + 1);
    if (k === 'g') add(P.gm, v, w);
    else if (k === 'a') add(P.au, v, w);
    else if (k === 'i') add(P.it, v, w);
    else if (k === 'l') add(P.ls, v, w);
  }
  function mine(text, w) {
    var raw = String(text || '').toLowerCase();
    var t = ' ' + raw.replace(/[_+|/.,]+/g, ' ').replace(/\s+/g, ' ') + ' ';
    var hits = [];
    WORDS.forEach(function (r) { if (r[1].test(t)) { apply(r[0], w); hits.push(r[0]); } });
    /* age band as written in ad set names: 25-34, 25 to 34, 25_34, age25-34, 18-65+ */
    var age = /(?:^|\D)(1[3-9]|[2-6]\d)\s?(?:-|to)\s?([2-6]\d|65)(?!\d)/.exec(t) || /(?:^|\D)(1[3-9]|[2-6]\d)\s([2-6]\d|65)(?!\d)/.exec(t);
    if (age && +age[2] > +age[1]) {
      P.ad.age = age[1] + '-' + age[2];
      if (/\s(f|fem)\s/.test(t)) { P.ad.gen = 'f'; add(P.au, 'her', 1.5); }
      else if (/\s(m)\s/.test(t)) { P.ad.gen = 'm'; add(P.au, 'him', 1); }
      var lo = +age[1];
      if (lo < 23) add(P.ls, 'student', 1.5); else if (lo >= 25) add(P.ls, 'pro', 1.5);
    } else {
      var plus = /(?:^|\D)(18|21|25|35|45|55)\s?(?:\+|plus\b)/.exec(raw);
      if (plus) P.ad.age = plus[1] + '+';
    }
    var geo = /\b(metro|tier ?1|tier ?2|tier ?3|mumbai|delhi|ncr|bangalore|bengaluru|pune|hyderabad|chennai|kolkata|ahmedabad|jaipur|lucknow|indore|nagpur)\b/.exec(t);
    if (geo) P.ad.geo = geo[1];
    return hits;
  }
  function refSrc(r) {
    if (!r) return '';
    var h = '';
    try { h = new URL(r).hostname.replace(/^www\./, ''); } catch (e) { return ''; }
    if (!h || h === location.hostname.replace(/^www\./, '')) return '';
    if (/instagram/.test(h)) return 'instagram';
    if (/facebook|fb\.com|fb\.me/.test(h)) return 'facebook';
    if (/(^|\.)google\./.test(h)) return 'google';
    if (/whatsapp|wa\.me/.test(h)) return 'whatsapp';
    if (/youtube|youtu\.be/.test(h)) return 'youtube';
    if (/(^|\.)t\.co$|twitter|(^|\.)x\.com$/.test(h)) return 'x';
    if (/pinterest/.test(h)) return 'pinterest';
    if (/bing\./.test(h)) return 'bing';
    if (/linkedin|lnkd/.test(h)) return 'linkedin';
    if (/chatgpt|openai|perplexity|gemini|copilot/.test(h)) return 'ai';
    if (/shopify|myshopify|checkout/.test(h)) return '';
    return h;
  }
  function uaInfo() {
    var ua = navigator.userAgent || '';
    return {
      ig: /Instagram/i.test(ua), fb: /FBAN|FBAV|FB_IAB|FBIOS/i.test(ua), wa: /WhatsApp/i.test(ua),
      os: /iPhone|iPad|iPod/i.test(ua) ? 'iOS' : /Android/i.test(ua) ? 'Android' : /Mac/i.test(ua) ? 'Mac' : /Win/i.test(ua) ? 'Windows' : 'Other',
      dev: (W.matchMedia && matchMedia('(pointer:coarse)').matches) ? (Math.min(screen.width, screen.height) >= 700 ? 'tablet' : 'mobile') : 'desktop'
    };
  }

  function readEntry() {
    var Q = new URLSearchParams(location.search);
    var ua = uaInfo();
    var dbg = Q.get('sy_debug');
    try { if (dbg === '1') sessionStorage.setItem('sy-mx-dbg', '1'); else if (dbg === '0') sessionStorage.removeItem('sy-mx-dbg'); } catch (e) {}
    var u = { s: Q.get('utm_source') || '', m: Q.get('utm_medium') || '', c: Q.get('utm_campaign') || '', ct: Q.get('utm_content') || '', t: Q.get('utm_term') || '' };
    var fb = Q.get('fbclid'), g = Q.get('gclid') || Q.get('gbraid') || Q.get('wbraid'), sy = Q.get('sy') || '';
    var ref = refSrc(D.referrer);
    var tagged = u.s || u.c || u.ct || u.t || fb || g || sy;
    if (tagged || (ref && newSession)) {
      var src = (u.s || (fb ? (ua.ig ? 'instagram' : 'facebook') : g ? 'google' : ref) || (ua.ig ? 'instagram' : ua.fb ? 'facebook' : 'direct')).toLowerCase();
      src = { ig: 'instagram', fb: 'facebook', msg: 'messenger', an: 'audience network', wa: 'whatsapp', yt: 'youtube' }[src] || src;
      var med = (u.m || (fb || g ? 'paid' : ref ? (/google|bing/.test(ref) ? 'organic' : 'referral') : '')).toLowerCase();
      var touch = { s: src, m: med, c: u.c, ct: u.ct, t: u.t, land: location.pathname, ts: NOW };
      if (!P.src.f) P.src.f = touch;
      P.src.l = touch;
      mine([u.c, u.ct, u.t, u.s].join(' '), 3);
      sy.toLowerCase().split(/[ ,]+/).forEach(function (k) {
        if (LABEL[k]) apply(k, 4);
        else if (/^(tee|reg|hoodie|sweat)$/.test(k)) apply('g:' + k, 4);
        else if (k === 'her' || k === 'him') apply('a:' + k, 3);
        else if (k === 'student' || k === 'pro') apply('l:' + k, 3);
        else if (k === 'gift' || k === 'deal') apply('i:' + k, 3);
      });
      if (/paid|cpc|ads?/.test(med)) P.fl.paid = 1;
    }
    if (!P.fl.lang) {
      P.fl.lang = 1;
      var langs = (navigator.languages || [navigator.language || '']).join(' ').toLowerCase();
      if (/\b(hi|mr)(-|\b)/.test(langs)) { add(P.th, 'hindi', 1); add(P.th, 'daily', 0.4); }
      try {
        var tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
        if (tz && !/Kolkata|Calcutta/.test(tz)) { P.fl.abroad = 1; add(P.th, 'hindi', 0.6); add(P.th, 'daily', 0.4); }
      } catch (e) {}
    }
    if (newSession) {
      var d = new Date(), h = d.getHours(), wd = d.getDay();
      if (wd > 0 && wd < 6 && h >= 10 && h < 18 && ua.dev === 'desktop') add(P.ls, 'pro', 0.4);
      if ((h >= 23 || h < 3) && ua.dev === 'mobile') add(P.ls, 'student', 0.2);
    }
    P.fl.dev = ua.dev; P.fl.os = ua.os;
  }

  function readPage() {
    var pt = cfg.pt;
    if (pt === 'collection' && cfg.col) {
      var land = P.ses.pv === 1;
      var sig = COLMAP[cfg.col] || (/^zodiac-/.test(cfg.col) ? 'zodiac' : '');
      if (sig && sig.indexOf('x:') !== 0) apply(sig, land ? 2 : 1);
      var Q = new URLSearchParams(location.search);
      Q.forEach(function (v, k) {
        if (/^filter\.v\.option\.size$/i.test(k)) add(P.sz, v.toUpperCase(), 1);
        if (/^filter\.v\.option\.colou?r$/i.test(k)) add(P.co, v.toLowerCase().replace(/\s+/g, '-'), 1);
      });
      if (Q.get('sort_by') === 'price-ascending') add(P.it, 'deal', 0.5);
    }
    if (pt === 'product' && cfg.prod) {
      var p = cfg.prod, land2 = P.ses.pv === 1;
      var w = land2 && P.src.l && P.src.l.ts === NOW ? 2.5 : 1.5;
      (p.th || '').split(' ').forEach(function (t) { if (t) add(P.th, t, t === 'funny' ? w * 0.5 : w); });
      add(P.gm, p.ty, 0.6); add(P.co, p.co, 0.4); add(P.pl, p.pl, 0.2);
      if (p.pr) { P.pr[0] += p.pr; P.pr[1] += 1; if (P.pr[1] > 30) { P.pr[0] = P.pr[0] / P.pr[1] * 15; P.pr[1] = 15; } }
      var seen = P.seen.filter(function (x) { return x.h !== p.h; });
      var prev = P.seen.filter(function (x) { return x.h === p.h; })[0];
      seen.unshift({ h: p.h, id: p.id, c: p.cols || '', t: p.t, u: p.u, i: p.i, p: p.pr, m: p.m, n: (prev ? prev.n : 0) + 1, ts: NOW });
      P.seen = seen.slice(0, 16);
      var t0 = Date.now();
      setTimeout(function () {
        if (D.visibilityState === 'visible') { (p.th || '').split(' ').forEach(function (t) { if (t) add(P.th, t, 0.8); }); dirty = true; save(); }
      }, 20000);
      var deep = false;
      W.addEventListener('scroll', function () {
        if (deep) return;
        var r = (W.scrollY + W.innerHeight) / Math.max(1, D.documentElement.scrollHeight);
        if (r > 0.6 && Date.now() - t0 > 4000) { deep = true; (p.th || '').split(' ').forEach(function (t) { if (t) add(P.th, t, 0.4); }); dirty = true; }
      }, { passive: true });
    }
    if (pt === 'search' && cfg.q) { mine(cfg.q, 2); P.q = [cfg.q].concat((P.q || []).filter(function (x) { return x !== cfg.q; })).slice(0, 5); }
    if (cfg.cust) {
      P.fl.cust = 1;
      P.fl.orders = cfg.cust.n || 0;
      if (cfg.cust.b && cfg.cust.b.length) P.bought = cfg.cust.b.slice(0, 40);
      if (newSession) (cfg.cust.tags || []).forEach(function (t) {
        var m = /^persona[-:](\w+)/i.exec(t); if (m) apply(m[1].toLowerCase(), 3);
        var l = /^life[-:](student|pro)/i.exec(t); if (l) add(P.ls, l[1].toLowerCase(), 2);
        /* "I mostly shop for" from the sign-up form */
        var w = /^shop[-:](him|her)$/i.exec(t); if (w) add(P.au, w[1].toLowerCase(), 3);
      });
    }
  }

  /* ---------- summary (the "mark") ---------- */
  function summary() {
    var tot = sum(P.th), ent = [];
    for (var k in P.th) { var v = dec(P.th[k]); if (v > 0.05) ent.push([k, v]); }
    ent.sort(function (a, b) { return b[1] - a[1]; });
    var share = function (v) { return tot ? v / tot : 0; };
    var persona = ent.length && ent[0][1] >= 2 && share(ent[0][1]) >= 0.3 ? ent[0][0] : '';
    var stu = dec(P.ls.student) + 0.8 * dec(P.th.college), pro = dec(P.ls.pro) + 0.8 * dec(P.th.office) + 0.4 * dec(P.th.adulting) + 0.3 * dec(P.th.money);
    var life = stu > pro * 1.4 && stu >= 1.5 ? 'student' : pro > stu * 1.4 && pro >= 1.5 ? 'pro' : '';
    var her = dec(P.au.her), him = dec(P.au.him);
    var forWho = her > him * 1.5 && her >= 1.5 ? 'her' : him > her * 1.5 && him >= 2 ? 'him' : '';
    var intent = P.bought.length || P.fl.orders ? 'buyer' : P.cart.length ? 'high' : (P.seen.length >= 3 || dec(P.it.wish) > 0) ? 'considering' : 'browsing';
    var f = P.src.f || {}, l = P.src.l || {};
    return {
      persona: persona, label: persona ? LABEL[persona] || persona : '',
      interests: ent.slice(0, 3).map(function (e) { return { k: e[0], p: Math.round(share(e[1]) * 100) }; }),
      life: life, forWho: forWho, age: P.ad.age || '', gen: P.ad.gen || '', geo: P.ad.geo || '',
      gift: dec(P.it.gift) >= 2, deal: dec(P.it.deal) >= 2,
      first: f.s ? [f.s, f.m, f.c, f.ct].filter(Boolean).join(' / ') : '', last: l.s ? [l.s, l.m, l.c].filter(Boolean).join(' / ') : '',
      landing: f.land || '', visits: P.s, intent: intent, device: [P.fl.dev, P.fl.os].filter(Boolean).join(' · ')
    };
  }

  /* ---------- scoring + diversity ---------- */
  function facts(el, rank, li) {
    var d = el.dataset;
    return {
      el: el, li: li || el, id: d.syId || d.handle || '', h: d.handle || '', dg: d.syDg || d.handle || '', ty: d.syTy || '', co: d.syCo || '',
      th: (d.syTh || '').split(' ').filter(Boolean), pr: +d.syPr || 0, cp: +d.syCp || 0, fl: d.syFl || '',
      sz: (d.sySz || '').split(' ').filter(Boolean), pl: d.syPl || '', d: +d.syD || 0, rank: rank
    };
  }
  function scorer(items, opts) {
    opts = opts || {};
    var N = items.length || 1, df = {};
    items.forEach(function (it) { it.th.forEach(function (t) { df[t] = (df[t] || 0) + 1; }); });
    /* themes that are on almost every product count for less, but a clear interest still leads */
    var idf = {};
    for (var t in df) idf[t] = 0.35 + 0.65 * Math.log(1 + N / df[t]) / Math.log(1 + N);
    items.forEach(function (it) {
      var best = '', bd = 1e9;
      it.th.forEach(function (t) { if (t !== 'funny' && df[t] < bd) { bd = df[t]; best = t; } });
      it.pt = best || it.th[0] || '';
    });
    var TH = norm(P.th, 2), GM = norm(P.gm, 2), CO = norm(P.co, 3), PL = norm(P.pl, 3);
    var her = dec(P.au.her), him = dec(P.au.him), herShare = her / (her + him + 1);
    var sz = topOf(P.sz), size = sz && sz.w >= 1 ? sz.k : '';
    var deal = Math.min(1, dec(P.it.deal) / 4);
    var avg = P.pr[1] >= 2 ? P.pr[0] / P.pr[1] : 0;
    var m = new Date().getMonth() + 1, winter = m >= 11 || m <= 2;
    var seed = P.id + '|' + new Date().toDateString();
    var bought = {}, incart = {}, seen = {};
    P.bought.forEach(function (h) { bought[h] = 1; });
    P.cart.forEach(function (h) { incart[h] = 1; });
    P.seen.forEach(function (x) { seen[x.h] = x; });
    var str = opts.strength != null ? opts.strength : STRENGTH;
    return function (it) {
      var aff = 0, why = [];
      it.th.forEach(function (t) { var a = (TH[t] || 0) * (idf[t] || 0.5); aff += a; if (a > 0.05) why.push(t); });
      aff += 0.6 * (GM[it.ty] || 0) + 0.3 * (CO[it.co] || 0) + 0.15 * (PL[it.pl] || 0);
      var s = str * aff;
      if (it.fl.indexOf('b') >= 0) s += 0.3;
      if (it.fl.indexOf('n') >= 0) s += 0.22;
      if (it.fl.indexOf('p') >= 0) s += 0.18;
      if (it.fl.indexOf('t') >= 0) s += 0.08;
      s += 0.08 * (1 - it.rank / N); /* the store's own order counts a little; it is mostly date added */
      if (winter && (it.ty === 'hoodie' || it.ty === 'sweat')) s += 0.1;
      if (!winter && it.ty === 'tee') s += 0.04;
      if (herShare > 0.6 && it.fl.indexOf('h') >= 0) s -= 0.35;
      if (size && it.sz.length && it.sz.indexOf(size) < 0) s -= 0.6;
      if (bought[it.h]) s -= 0.9;
      if (incart[it.h]) s -= 0.45;
      var sn = seen[it.h];
      if (sn) { if (sn.n >= 4) s -= 0.12; else if (NOW - sn.ts < 3 * DAY) s += 0.08; }
      if (deal > 0 && it.cp > it.pr) s += 0.25 * deal;
      if (avg && it.pr) s -= 0.1 * Math.abs(Math.log(it.pr / avg));
      if (opts.avoid && opts.avoid[it.dg]) s -= 0.6 * Math.min(2, opts.avoid[it.dg]); /* already shown higher up the page */
      s += 0.18 * rand(seed, it.dg);
      it.why = why;
      return s;
    };
  }
  /* Greedy re-ranking: best score first, but the same design never within 4 places, and garment, main theme
     and colour are spread out. With opts.top (the visitor's main interest) every opts.every-th place goes to the
     best product outside that interest, so a focused visitor still sees some of the rest of the range. */
  function diversify(items, score, opts) {
    opts = opts || {};
    items.forEach(function (it) { it.s = score(it); });
    var rest = items.slice().sort(function (a, b) { return b.s - a.s; }), out = [];
    var top = opts.top || '', every = opts.every || 0;
    function pick(explore) {
      var bi = -1, bv = -1e9, lim = explore ? rest.length : Math.min(rest.length, 40);
      for (var i = 0; i < lim; i++) {
        var it = rest[i], pen = 0;
        if (explore && it.th.indexOf(top) >= 0) continue;
        for (var d = 1; d <= 6 && d <= out.length; d++) {
          var p = out[out.length - d];
          if (p.dg === it.dg) pen += d <= 4 ? 50 : 0.6 / d;
          if (d <= 2 && p.ty === it.ty) pen += 0.06;
          if (d <= 2 && it.pt && p.pt === it.pt) pen += 0.05;
          if (d === 1 && p.co === it.co) pen += 0.05;
        }
        var v = it.s - pen;
        if (v > bv) { bv = v; bi = i; }
      }
      return bi;
    }
    while (rest.length) {
      var bi = top && every && out.length % every === every - 1 ? pick(true) : -1;
      if (bi < 0) bi = pick(false);
      out.push(rest.splice(bi, 1)[0]);
    }
    return out;
  }
  var EVERY = { gentle: 4, balanced: 5, strong: 7 }[cfg.str || 'balanced'] || 5;
  function order(items, opts) {
    opts = opts || {};
    var o = { strength: opts.strength, avoid: opts.avoid };
    if (opts.explore) { var sm = summary(); if (sm.persona) { o.top = sm.persona; o.every = EVERY; } }
    return diversify(items, scorer(items, o), o);
  }

  /* ---------- collection grid ---------- */
  function debugOn() { try { return sessionStorage.getItem('sy-mx-dbg') === '1'; } catch (e) { return false; } }
  function tagScores(list) {
    if (!debugOn()) return;
    list.forEach(function (it, i) {
      var m = it.el.querySelector('.sy-card__m');
      if (!m || m.querySelector('.sy-mx-dbg-s')) return;
      var b = D.createElement('span');
      b.className = 'sy-mx-dbg-s';
      b.textContent = '#' + (i + 1) + ' · ' + (it.s || 0).toFixed(2) + (it.why && it.why.length ? ' · ' + it.why.join(',') : '');
      m.appendChild(b);
    });
  }
  function eager(lis, n) {
    lis.slice(0, n).forEach(function (li) { var img = li.querySelector('img.sy-card__pri[loading="lazy"]'); if (img) img.loading = 'eager'; });
  }
  /* Grid state for the Back button: the order and how many were shown, per collection + filter state, for this
     tab only (sessionStorage). Reused only on back/forward and reload, so a fresh visit gets a fresh order. */
  var NAV = (function () { try { var e = performance.getEntriesByType('navigation')[0]; return e ? e.type : ''; } catch (x) { return ''; } })();
  var RESTORE = NAV === 'back_forward' || NAV === 'reload';
  W.addEventListener('popstate', function () { RESTORE = true; }); /* Back between filter states on the same page */
  function stateKey() {
    var keep = [];
    new URLSearchParams(location.search).forEach(function (v, k) { if (/^(filter\.|sort_by$|page$|q$)/.test(k)) keep.push(k + '=' + v); });
    return 'sy-mx-o:' + location.pathname + '?' + keep.sort().join('&');
  }
  function readState() { try { return JSON.parse(sessionStorage.getItem(stateKey()) || 'null'); } catch (e) { return null; } }
  function writeState(st) { try { sessionStorage.setItem(stateKey(), JSON.stringify(st)); } catch (e) {} }
  function reveal(ul, lis, st) {
    var per = Math.max(8, +cfg.page || 24), shown = Math.max(per, (st && st.n) || 0);
    var wrap = ul.parentNode;
    var box = wrap && wrap.querySelector('[data-sy-mx-more]');
    var btn = box && box.querySelector('[data-sy-mx-more-btn]'), cnt = box && box.querySelector('[data-sy-mx-count]');
    var pag = wrap && wrap.querySelector('.pagination-wrapper');
    var total = Math.max(+ul.getAttribute('data-sy-total') || 0, lis.length);
    function paint() {
      lis.forEach(function (li, i) { li.classList.toggle('sy-mx-hide', i >= shown); });
      var done = shown >= lis.length;
      if (box) {
        box.hidden = done;
        if (cnt) cnt.textContent = 'Showing ' + Math.min(shown, lis.length) + ' of ' + total;
      }
      if (pag) pag.hidden = !done; /* page 2 link only once this page is fully shown */
    }
    paint();
    if (!box || box.hidden) return;
    /* loads two more steps by itself as the visitor scrolls, then waits for the button so the footer stays reachable */
    var auto = 0;
    function more() {
      if (shown >= lis.length) return;
      shown += per; paint();
      if (st) { st.n = shown; writeState(st); }
    }
    if (btn) btn.addEventListener('click', more);
    if ('IntersectionObserver' in W) {
      var io = new IntersectionObserver(function (es) {
        if (!es[0].isIntersecting) return;
        more(); auto++;
        if (shown >= lis.length || auto >= 2) io.disconnect();
      }, { rootMargin: '0px 0px 600px 0px' });
      setTimeout(function () { io.observe(box); }, 600);
    }
  }
  /* Adds "Recommended for you" to the sort menu. Picking any other option (including the store's default)
     gives that exact order; picking this one comes back to the personal order. */
  function sortOption(on) {
    $$('select[name="sort_by"]').forEach(function (sel) {
      var o = sel.querySelector('option[data-sy-rec]');
      if (!on) { if (o) o.remove(); return; }
      if (!o) {
        o = D.createElement('option');
        o.value = '';
        o.setAttribute('data-sy-rec', '');
        o.textContent = 'Recommended for you';
        sel.insertBefore(o, sel.firstChild);
      }
      sel.value = '';
    });
  }
  function applyGrid(ul) {
    if (!ul || ul.__sy) return;
    ul.__sy = 1;
    try {
      var lis = Array.prototype.slice.call(ul.children);
      var items = [];
      lis.forEach(function (li, i) { var c = li.querySelector('[data-sy-card]'); if (c) items.push(facts(c, i, li)); });
      var sp = new URLSearchParams(location.search).get('sort_by');
      var personal = STRENGTH > 0 && !sp;
      var old = RESTORE ? readState() : null, st = { o: null, n: 0 };
      if (old && old.n) st.n = old.n;
      if (personal && items.length > 1) {
        var out = null;
        if (old && old.o && old.o.length === items.length && !debugOn()) {
          var byId = {};
          items.forEach(function (it) { byId[it.id] = it; });
          if (old.o.every(function (id) { return byId[id]; })) out = old.o.map(function (id) { return byId[id]; });
        }
        if (!out) out = order(items, { explore: true });
        st.o = out.map(function (it) { return it.id; });
        var frag = D.createDocumentFragment();
        out.forEach(function (it) { frag.appendChild(it.li); });
        ul.appendChild(frag);
        tagScores(out);
        lis = out.map(function (it) { return it.li; });
      }
      writeState(st);
      sortOption(personal);
      lis.forEach(function (li, i) { if (li.hasAttribute('data-cascade')) li.style.setProperty('--animation-order', (i % 8) + 1); });
      eager(lis, 4);
      reveal(ul, lis, st);
    } catch (e) {}
    ul.classList.remove('sy-mx-pending');
    RESTORE = false; /* filter changes after this are fresh states */
  }
  SYM.grid = function (ul) {
    applyGrid(ul);
    var box = ul && (ul.closest('#ProductGridContainer') || ul.parentNode);
    if (box && !box.__syObs && 'MutationObserver' in W) {
      box.__syObs = 1;
      new MutationObserver(function () {
        var u = D.getElementById('product-grid');
        if (u && u.hasAttribute('data-sy-mx') && !u.__sy) applyGrid(u);
      }).observe(box, { childList: true, subtree: true });
    }
  };

  /* ---------- homepage rails ---------- */
  function uniq(root, suf) {
    /* attributes, not el.id: on a <form> with an input named "id", form.id is that input */
    $$('[id]', root).forEach(function (el) { el.setAttribute('id', el.getAttribute('id') + suf); });
    $$('[aria-controls]', root).forEach(function (el) { el.setAttribute('aria-controls', el.getAttribute('aria-controls') + suf); });
  }
  function fetchCards(url) {
    var u = url + (url.indexOf('?') >= 0 ? '&' : '?') + 'section_id=sy-rail-cards';
    return fetch(u, { credentials: 'same-origin' }).then(function (r) { if (!r.ok) throw 0; return r.text(); }).then(function (html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      return $$('[data-sy-rail-cards] > li', doc).filter(function (li) { return li.querySelector('[data-sy-card]'); });
    });
  }
  /* after re-ordering a sideways rail, start it at the first card (scroll snapping would otherwise follow the card that used to be first) */
  function railStart(ul) {
    try {
      ul.style.scrollSnapType = 'none';
      ul.scrollLeft = 0;
      void ul.offsetWidth;
      ul.style.scrollSnapType = '';
    } catch (e) {}
  }
  function fill(rail, lis, n, sufBase) {
    var ul = rail.querySelector('.sy-rail');
    if (!ul) return;
    var seenH = {}, items = [];
    lis.forEach(function (li, i) {
      var c = li.querySelector('[data-sy-card]');
      if (!c || seenH[c.dataset.handle]) return;
      seenH[c.dataset.handle] = 1;
      items.push(facts(c, i, li));
    });
    if (!items.length) { rail.hidden = true; return; }
    var out = STRENGTH > 0 ? order(items, { avoid: PAGE_DG }) : items;
    ul.innerHTML = '';
    out.slice(0, n).forEach(function (it, i) {
      var li = D.importNode(it.li, true);
      uniq(li, '-' + sufBase + i);
      ul.appendChild(li);
      PAGE_DG[it.dg] = (PAGE_DG[it.dg] || 0) + 1;
    });
    tagScores(out.slice(0, n).map(function (it, i) { return { el: ul.children[i].querySelector('[data-sy-card]'), s: it.s, why: it.why }; }));
    railStart(ul);
    rail.classList.add('is-loaded');
    D.dispatchEvent(new CustomEvent('sy:cards', { detail: { root: ul } }));
  }
  var railN = 0;
  /* designs already on the page, so each rail brings different ones */
  var PAGE_DG = {};
  function notePage() {
    $$('[data-sy-card][data-sy-dg]').forEach(function (c) {
      if (c.closest('[data-sy-mxr]')) return;
      var r = c.getBoundingClientRect();
      if (!r.width) return; /* only cards a visitor can see */
      PAGE_DG[c.dataset.syDg] = (PAGE_DG[c.dataset.syDg] || 0) + 1;
    });
  }
  function loadRail(rail) {
    if (rail.__sy) return;
    rail.__sy = 1;
    var kind = rail.getAttribute('data-sy-mxr'), n = +rail.getAttribute('data-n') || 10, suf = 'm' + (++railN) + '-';
    if (kind === 'collection') {
      var src = rail.getAttribute('data-src');
      if (!src) return;
      fetchCards(src).then(function (lis) { fill(rail, lis, n, suf); }).catch(function () { rail.hidden = true; });
    } else if (kind === 'for_you') {
      var sm = summary(), cols = [];
      sm.interests.forEach(function (x) { if (THCOL[x.k] && cols.length < 2 && x.p >= 15) cols.push(THCOL[x.k]); });
      var g = topOf(P.gm);
      if (g && g.w >= 1.5 && THCOL[g.k] && cols.indexOf(THCOL[g.k]) < 0) cols.push(THCOL[g.k]);
      if (!cols.length || !sm.interests.length) { rail.hidden = true; return; }
      var sub = rail.querySelector('[data-sy-mxr-sub]');
      if (sub) {
        var names = sm.interests.filter(function (x) { return x.p >= 15 && LIKE[x.k]; }).slice(0, 2).map(function (x) { return LIKE[x.k]; });
        if (names.length) sub.textContent = 'Because you like ' + names.join(' and ') + '.';
      }
      Promise.all(cols.map(function (h) { return fetchCards(ROOT + 'collections/' + h).catch(function () { return []; }); })).then(function (res) {
        var all = [];
        res.forEach(function (l) { all = all.concat(l); });
        var cur = cfg.prod && cfg.prod.h;
        all = all.filter(function (li) { var c = li.querySelector('[data-sy-card]'); return c && c.dataset.handle !== cur && P.bought.indexOf(c.dataset.handle) < 0; });
        rail.hidden = false;
        fill(rail, all, n, suf);
      });
    }
  }
  function renderRecent(rail) {
    var n = +rail.getAttribute('data-n') || 10, cur = cfg.prod && cfg.prod.h;
    var list = P.seen.filter(function (x) { return x.h && x.h !== cur && x.u && P.bought.indexOf(x.h) < 0; }).slice(0, n);
    if (list.length < 2) { rail.hidden = true; return; }
    var ul = rail.querySelector('.sy-rail');
    ul.innerHTML = list.map(function (x) {
      var img = x.i ? '<img class="sy-card__pri" src="' + esc(x.i) + '" alt="" loading="lazy" width="533" height="592">' : '<span class="sy-card__ph" aria-hidden="true"></span>';
      var data = x.id && x.p ? ' data-sy-card data-sy-id="' + esc(x.id) + '" data-sy-pr="' + esc(x.p) + '" data-sy-cols="' + esc(x.c || '') + '"' : '';
      return '<li><div class="card-wrapper product-card-wrapper sy-card sy-card--mini" data-handle="' + esc(x.h) + '"' + data + '>' +
        '<div class="sy-card__m"><a class="sy-card__img" style="padding-bottom:111.11%" href="' + esc(x.u) + '" tabindex="-1" aria-hidden="true">' + img + '</a></div>' +
        '<div class="sy-card__t"><h3 class="sy-card__name"><a class="full-unstyled-link" href="' + esc(x.u) + '">' + esc(x.t) + '</a></h3>' +
        (x.m ? '<span class="sy-card__meta">' + esc(x.m) + '</span>' : '') +
        (x.p ? '<div class="sy-card__price"><div class="price"><span class="price-item price-item--regular">' + esc(money(x.p)) + '</span></div></div>' : '') +
        (data ? '<p class="sy-card__eff" data-sy-eff hidden></p>' : '') +
        '</div></div></li>';
    }).join('');
    rail.hidden = false;
    railStart(ul);
    rail.classList.add('is-loaded');
    D.dispatchEvent(new CustomEvent('sy:cards', { detail: { root: ul } }));
  }
  function railTheme(rail) {
    var c = rail.getAttribute('data-col') || '';
    return rail.getAttribute('data-th') || COLMAP[c] || (/^zodiac-/.test(c) ? 'zodiac' : '');
  }
  function orderRails(wrap) {
    if (STRENGTH <= 0) return;
    var rails = $$(':scope > [data-sy-mxr]', wrap);
    if (rails.length < 2) return;
    var TH = norm(P.th, 2), GM = norm(P.gm, 2), sm = summary();
    if (!sm.interests.length || sm.interests[0].p < 20) return;
    var scored = rails.map(function (r, i) {
      var t = railTheme(r), v = -1;
      if (r.getAttribute('data-sy-mxr') === 'collection') {
        if (t.indexOf('g:') === 0) v = 0.7 * (GM[t.slice(2)] || 0);
        else if (t) v = TH[t] || 0;
        else v = 0;
      }
      return { r: r, v: v, i: i };
    });
    var moved = scored.filter(function (x) { return x.v > 0.12; }).sort(function (a, b) { return b.v - a.v || a.i - b.i; });
    if (!moved.length) return;
    var rest = scored.filter(function (x) { return moved.indexOf(x) < 0; });
    var after = rails[rails.length - 1].nextSibling;
    moved.concat(rest).forEach(function (x) { wrap.insertBefore(x.r, after); });
  }
  function initRails() {
    if (W.Shopify && Shopify.designMode) return; /* the theme editor shows the rails as set up */
    if (!D.querySelector('[data-sy-mxr]')) return;
    notePage();
    $$('[data-sy-mxr-wrap]').forEach(orderRails);
    var rails = $$('[data-sy-mxr]');
    rails.forEach(function (r) {
      var k = r.getAttribute('data-sy-mxr');
      if (k === 'recent') renderRecent(r);
      else if (k === 'for_you') loadRail(r); /* hidden until filled, so it cannot wait for scrolling */
      else if ((k === 'deals' || k === 'low_stock') && STRENGTH > 0) {
        var ul = r.querySelector('.sy-rail'), items = [];
        if (!ul) return;
        Array.prototype.slice.call(ul.children).forEach(function (li, i) { var c = li.querySelector('[data-sy-card]'); if (c) items.push(facts(c, i, li)); });
        if (items.length > 1) { order(items).forEach(function (it) { ul.appendChild(it.li); }); railStart(ul); }
      }
    });
    var lazy = rails.filter(function (r) { return r.getAttribute('data-sy-mxr') === 'collection' && r.getAttribute('data-src'); });
    if (!lazy.length) return;
    if (!('IntersectionObserver' in W)) { lazy.forEach(loadRail); return; }
    var io = new IntersectionObserver(function (es) {
      es.forEach(function (e) { if (e.isIntersecting) { io.unobserve(e.target); loadRail(e.target); } });
    }, { rootMargin: '1200px 0px 1200px 0px' });
    lazy.forEach(function (r) { io.observe(r); });
  }

  /* ---------- related products on the product page ---------- */
  function related() {
    /* only "You may also like"; never the "Also available as" block, which lists the same design on purpose */
    var box = D.querySelector('product-recommendations.related-products');
    if (!box || !('MutationObserver' in W)) return;
    function run() {
      var ul = box.querySelector('ul');
      if (!ul || ul.__sy) return;
      var lis = Array.prototype.slice.call(ul.children), items = [];
      lis.forEach(function (li, i) { var c = li.querySelector('[data-sy-card]'); if (c) items.push(facts(c, i, li)); });
      if (items.length < 2) return;
      ul.__sy = 1;
      var cur = cfg.prod || {};
      var keep = items.filter(function (it) { return it.dg !== cur.dg && it.h !== cur.h; });
      var drop = items.filter(function (it) { return keep.indexOf(it) < 0; });
      var list = keep.length >= 4 ? keep : keep.concat(drop);
      var out = STRENGTH > 0 ? order(list) : list;
      drop.forEach(function (it) { if (list.indexOf(it) < 0) it.li.remove(); });
      out.forEach(function (it) { ul.appendChild(it.li); });
      railStart(ul);
      tagScores(out);
    }
    new MutationObserver(run).observe(box, { childList: true, subtree: true });
    run();
  }

  /* ---------- product page: stickers over the main photo ---------- */
  function pdpStickers() {
    var st = D.querySelector('.product__info-container .sy-stk--pdp');
    var host = D.querySelector('.product__media-wrapper media-gallery') || D.querySelector('.product__media-wrapper');
    if (!st || !host) return;
    host.classList.add('sy-pdp-stk-host');
    host.appendChild(st);
  }

  /* ---------- actions ---------- */
  function cardFacts(h) {
    if (!h) return null;
    if (cfg.prod && cfg.prod.h === h) return { th: (cfg.prod.th || '').split(' ').filter(Boolean), ty: cfg.prod.ty, co: cfg.prod.co };
    var c = D.querySelector('[data-sy-card][data-handle="' + h.replace(/"/g, '') + '"]');
    return c ? facts(c, 0) : null;
  }
  function boost(f, w) {
    if (!f) return;
    f.th.forEach(function (t) { add(P.th, t, t === 'funny' ? w * 0.5 : w); });
    add(P.gm, f.ty, w * 0.5); add(P.co, f.co, w * 0.25);
    dirty = true;
  }
  function onCart(ev) {
    try {
      var d = ev && (ev.cartData || ev.data && ev.data.cartData);
      if (!d || ev.source === 'cart-items') return;
      var items = d.items || (d.handle ? [d] : []);
      items.forEach(function (it) {
        if (!it.handle) return;
        boost(cardFacts(it.handle), 4);
        if (P.cart.indexOf(it.handle) < 0) P.cart.unshift(it.handle);
        (it.variant_options || []).forEach(function (o) { if (SIZE_RE.test(o)) add(P.sz, String(o).toUpperCase(), 2); });
      });
      P.cart = P.cart.slice(0, 20);
      dirty = true; save();
      setTimeout(syncCart, 1500);
    } catch (e) {}
  }
  D.addEventListener('click', function (e) {
    var t = e.target && e.target.closest ? e.target : null;
    if (!t) return;
    var el;
    if ((el = t.closest('[data-sy-wish]'))) {
      var h = el.getAttribute('data-sy-wish');
      setTimeout(function () { if (el.classList.contains('is-on')) { boost(cardFacts(h), 3); add(P.it, 'wish', 1); save(); } }, 50);
    } else if ((el = t.closest('[data-sy-qa-variant]'))) {
      var v = (el.textContent || '').trim();
      if (SIZE_RE.test(v)) { add(P.sz, v.toUpperCase(), 1); dirty = true; }
    } else if (t.closest('.sy-off__copy') || t.closest('[data-sy-act="apply"]')) {
      add(P.it, 'deal', 1); dirty = true;
    }
  }, true);
  function subscribeDawn() {
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        subscribe(PUB_SUB_EVENTS.cartUpdate, onCart);
        if (PUB_SUB_EVENTS.variantChange) subscribe(PUB_SUB_EVENTS.variantChange, function (ev) {
          var v = ev && ev.data && ev.data.variant;
          if (!v || !v.options) return;
          v.options.forEach(function (o) { if (SIZE_RE.test(o)) add(P.sz, String(o).toUpperCase(), 1); });
          dirty = true;
        });
      }
    } catch (e) {}
  }

  /* ---------- marking: bag attributes + analytics ---------- */
  var LIFE = { student: 'Student (likely)', pro: 'Working professional (likely)' };
  var INTENT = { buyer: 'Customer', high: 'High: added to bag', considering: 'Considering', browsing: 'Browsing' };
  function attrs() {
    var s = summary(), a = {};
    a._sy_persona = s.label || 'Mixed interests';
    if (s.interests.length) a._sy_interests = s.interests.map(function (x) { return (LABEL[x.k] || x.k) + ' ' + x.p + '%'; }).join(', ');
    if (s.life) a._sy_life_stage = LIFE[s.life];
    if (s.forWho) a._sy_shopping_for = s.forWho === 'her' ? 'Her' : 'Him';
    if (s.age) a._sy_age_band = s.age + ' (from ad)';
    if (s.gift) a._sy_intent_gift = 'Yes';
    if (s.first) a._sy_first_source = s.first.slice(0, 200);
    if (s.last && s.last !== s.first) a._sy_last_source = s.last.slice(0, 200);
    if (s.landing) a._sy_landing = s.landing.slice(0, 200);
    a._sy_visits = String(s.visits);
    a._sy_intent = INTENT[s.intent];
    if (s.device) a._sy_device = s.device;
    return a;
  }
  var synced = false;
  function syncCart() {
    if (canMark !== true) return;
    var a = attrs(), key = JSON.stringify(a);
    fetch(ROOT + 'cart.js', { credentials: 'same-origin', headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (c) {
      if (!c || !c.item_count) return;
      var cur = c.attributes || {}, same = true;
      for (var k in a) if (cur[k] !== a[k]) same = false;
      if (same) return;
      return fetch(ROOT + 'cart/update.js', { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ attributes: a }) });
    }).then(function () { P.mk = key; dirty = true; save(); }).catch(function () {});
  }
  function mark() {
    if (canMark !== true) return;
    var s = summary();
    var props = { sy_persona: s.persona || 'mixed', sy_life_stage: s.life || 'unknown', sy_shop_for: s.forWho || 'unknown', sy_age_band: s.age || 'unknown', sy_first_source: (P.src.f && P.src.f.s) || 'direct', sy_intent: s.intent };
    try {
      W.dataLayer = W.dataLayer || [];
      if (!SYM.__dl) { SYM.__dl = 1; W.dataLayer.push({ event: 'sy_profile', sy_profile: props }); }
      if (typeof W.gtag === 'function') W.gtag('set', 'user_properties', props);
    } catch (e) {}
    if (!synced && D.querySelector('#cart-icon-bubble .cart-count-bubble')) { synced = true; setTimeout(syncCart, 2500); }
  }

  /* ---------- debug panel (?sy_debug=1) ---------- */
  function panel() {
    if (!debugOn() || D.getElementById('sy-mx-dbg')) return;
    var s = summary(), box = D.createElement('div');
    box.id = 'sy-mx-dbg';
    var bars = s.interests.map(function (x) { return '<li><span>' + esc(LABEL[x.k] || x.k) + '</span><i style="width:' + x.p + '%"></i><b>' + x.p + '%</b></li>'; }).join('');
    box.innerHTML = '<p><b>Smart ordering</b> <button type="button" data-x>Close</button></p>' +
      '<dl><dt>Persona</dt><dd>' + esc(s.label || '—') + '</dd><dt>Life stage</dt><dd>' + esc(LIFE[s.life] || '—') + '</dd>' +
      '<dt>Shopping for</dt><dd>' + esc(s.forWho || '—') + '</dd><dt>Age band (ad)</dt><dd>' + esc(s.age || '—') + '</dd>' +
      '<dt>First source</dt><dd>' + esc(s.first || '—') + '</dd><dt>Last source</dt><dd>' + esc(s.last || '—') + '</dd>' +
      '<dt>Visits · intent</dt><dd>' + s.visits + ' · ' + esc(INTENT[s.intent]) + '</dd><dt>Device</dt><dd>' + esc(s.device) + '</dd>' +
      '<dt>Saved</dt><dd>' + (canStore === true ? 'yes (privacy allows)' : canStore === false ? 'no (privacy declined)' : 'waiting for privacy choice') + '</dd>' +
      '<dt>Marked</dt><dd>' + (canMark === true ? 'yes (analytics allowed)' : canMark === false ? 'no (analytics declined)' : 'waiting for privacy choice') + '</dd></dl>' +
      (bars ? '<ul>' + bars + '</ul>' : '') + '<p><button type="button" data-r>Reset my profile</button></p>';
    D.body.appendChild(box);
    box.querySelector('[data-x]').onclick = function () { box.remove(); try { sessionStorage.removeItem('sy-mx-dbg'); } catch (e) {} };
    box.querySelector('[data-r]').onclick = function () { try { localStorage.removeItem(KEY); } catch (e) {} P = blank(); location.reload(); };
  }

  /* ---------- boot ---------- */
  try { readEntry(); readPage(); } catch (e) {}
  SYM.profile = function () { return summary(); };
  SYM.attrs = attrs;
  SYM._core = { scorer: scorer, diversify: diversify, order: order, facts: facts, mine: mine, refSrc: refSrc, summary: summary, setProfile: function (p) { P = p; }, getProfile: function () { return P; } };
  function boot() {
    try { subscribeDawn(); } catch (e) {}
    try { initRails(); } catch (e) {}
    try { related(); } catch (e) {}
    try { pdpStickers(); } catch (e) {}
    try { watchConsent(); } catch (e) {}
    try { panel(); } catch (e) {}
  }
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', boot); else boot();
})();
